import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DomSanitizer } from '@angular/platform-browser'

import { first as _first, some as _some } from 'lodash/fp'
import { ImageModule } from 'primeng/image'

import { Observable, Subject, finalize, from, map, mergeMap, takeUntil, toArray } from 'rxjs'

import { FlxButtonModule, FlxMessageComponent } from '@flowx/angular-ui-toolkit'
import { FlxFileSizePipe } from '@flowx/designer/pipes/size.pipe'
import { DocumentPluginApi } from '@flowx/designer/shared/api/document-plugin.api'
import { convertBlobToBase64 } from '@flowx/designer/shared/utils/file.utils'

import { resetInputFileValueFromEvent } from '../../../common/ai.utils'
import {
  FileExtension,
  FileMimeType,
  FileMimeTypeLabel,
  UploadContextFile,
  imageFileTypes,
} from '../../../types/ai.types'

@Component({
  selector: 'flx-file-source-upload',
  imports: [CommonModule, FlxFileSizePipe, ImageModule, FlxButtonModule, FlxMessageComponent],
  templateUrl: './file-source-upload.component.html',
  styleUrl: './file-source-upload.component.scss',
  providers: [FlxFileSizePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxFileSourceUploadComponent {
  #sanitizer = inject(DomSanitizer)
  #documentPluginApi = inject(DocumentPluginApi)
  #destroyRef = inject(DestroyRef)
  #fileSizePipe = inject(FlxFileSizePipe)
  #cancelClick$ = new Subject<void>()
  #files = linkedSignal(() => this.initialFiles())
  #file = computed(() => _first(this.#files()))
  #imageFiles = computed(() =>
    this.#files().filter((file): file is UploadContextFile =>
      imageFileTypes.includes(file.type as FileMimeType)
    )
  )
  #imageFile = computed(() => _first(this.#imageFiles()))

  config = input.required<{
    allowedFileTypes: string[]
    isMultipleUploadAllowed: boolean
    maxFileSize?: number
    defaultImage: string
    uploadButtonText: string
    noFileUploadedText?: string
  }>()
  initialFiles = input<UploadContextFile[]>([])

  fileSelected = output<UploadContextFile[]>()
  uploadError = output<string | null>()

  loading = signal(false)
  error = signal<string>(null)
  hasUploadedFiles = computed(() => !!this.#files()?.length)
  fileName = computed(() => this.#file()?.name)
  fileSize = computed(() => this.#file()?.file?.size)

  fileType = computed(
    () =>
      FileMimeTypeLabel[this.#file()?.type] ||
      this.#file()?.type ||
      (this.fileName().includes(FileExtension.BPMN) ? FileExtension.BPMN : '')
  )

  supportedFormats = computed(() => {
    const listFormat = new Intl.ListFormat('en', { style: 'long', type: 'disjunction' })
    return listFormat.format(
      this.config()
        .allowedFileTypes.map((type) => FileMimeTypeLabel[type])
        .filter(Boolean)
    )
  })

  maxFileSize = computed(() => this.config().maxFileSize)

  imageSrc = computed(() =>
    this.#imageFile() ? convertBlobToBase64(this.#imageFile().file) : undefined
  )

  imageName = computed(() => this.#imageFile()?.name)
  defaultImage = computed(() => this.#sanitizer.bypassSecurityTrustHtml(this.config().defaultImage))

  constructor() {
    effect(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const config = this.config()

      this.removeUploadedFiles()
    })

    effect(() => {
      const files = this.#files()

      this.fileSelected.emit(files)
    })

    effect(() => {
      const error = this.error()

      this.uploadError.emit(error)
    })
  }

  onFileSelected(event: Event): void {
    this.error.set(null)
    const files = Array.from((event.target as HTMLInputElement).files)

    if (this.maxFileSize() && _some((file) => file.size > this.maxFileSize(), files)) {
      this.error.set(
        `File size too large. Please select a file up to ${this.#fileSizePipe.transform(this.maxFileSize())}`
      )
      resetInputFileValueFromEvent(event)
      return
    }
    this.loading.set(true)

    from(files)
      .pipe(
        mergeMap((file: File) =>
          this.#uploadFile(file).pipe(
            takeUntil(this.#cancelClick$),
            map((id) => ({
              id,
              file,
              name: file.name,
              type: file.type,
            }))
          )
        ),
        toArray(),
        takeUntil(this.#cancelClick$),
        finalize(() => {
          this.loading.set(false)
          resetInputFileValueFromEvent(event)
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: (uploadedFiles) => this.#files.set(uploadedFiles),
        error: (error) => {
          if (error?.status === 0) {
            this.error.set(
              `Upload failed. The file may exceed the server's maximum size limit. Please try a smaller file.`
            )
          } else if (error?.status === 413) {
            this.error.set(`File is too large for the server. Please select a smaller file.`)
          } else if (typeof error === 'string') {
            this.error.set(error)
          } else if (error?.message) {
            this.error.set(error.message)
          } else {
            this.error.set('An unknown error occurred during upload')
          }
        },
      })
  }

  cancelUpload(): void {
    this.#cancelClick$.next()
  }

  removeUploadedFiles(): void {
    this.#files.set([])
    this.error.set(null)
  }

  #uploadFile(file: File): Observable<string> {
    return this.#documentPluginApi.uploadTempFile(file).pipe(map(({ id }) => id))
  }
}
